import React, { useContext, useEffect, useState } from "react";
import "./style.css";
import {
  Button,
  Collapse,
  Modal,
  Input,
  Select,
  Form,
  message,
  Popconfirm,
  Radio,
  notification,
} from "antd";
import { SearchOutlined } from "@ant-design/icons";
import { CloseCircleOutlined, UserOutlined } from "@ant-design/icons";
import {
  alterUserStatus,
  createUser,
  editUser,
  getCompanyList,
  getUsersList,
  bulkAssignParentCompany,
  deleteUser,
  loginAsUser,
  pageAccess,
} from "../../../API/Admin/user";
import {
  CaretDownOutlined,
  CaretUpOutlined,
  DeleteOutlined,
} from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import { encryptText } from "../../../Utils/Encryption";
import updateUserStates from "../../../Utils/UpdateUsersState";
import { UserContext } from "../../../App";
import { checkApiKeyValidity } from "../../../API/auth";

const { Panel } = Collapse;
const { Option } = Select;

const Companies = () => {
  const [users, setUsers] = useState([]);
  const [allUsers, setAllUsers] = useState([]);
  const {
    userData,
    setUserPages,
    setUserToken,
    setUserData,
    setUserApiKeyStatus,
  } = useContext(UserContext);

  const [isModalVisible, setIsModalVisible] = useState(false);
  const [isModalVisibleEdit, setIsModalVisibleEdit] = useState(false);
  const [loading, setLoading] = useState(false);
  const [editId, setEditId] = useState();
  const [editName, setEditName] = useState("");
  const [editRole, setEditRole] = useState("");
  const [editEmail, setEditEmail] = useState("");
  const [editParentCompany, setEditParentCompany] = useState("");
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [isAssigning, setIsAssigning] = useState(false);
  const [loadingDelete, setLoadingDelete] = useState(false);
  const [activeKey, setActiveKey] = useState(null);

  const navigate = useNavigate();

  const [form] = Form.useForm();
  const [form2] = Form.useForm();

  const pages = [
    { id: 1, value: "dashboard", name: "Dashboard" },
    { id: 2, value: "mileage", name: "Mileage" },
    { id: 3, value: "fuel", name: "Fuel" },
    { id: 4, value: "idle", name: "Idle" },
    { id: 5, value: "fuel_efficiency", name: "Fuel Efficiency" },
    { id: 6, value: "sensor", name: "Sensor" },
    { id: 7, value: "servicing_reminder", name: "Servicing & Reminder" },
    { id: 8, value: "decision_engine", name: "Decision Engine" },
    { id: 9, value: "more_analysis", name: "More Analysis" },
  ];

  const checkPageAccess = (page, pages) => {
    return pages?.includes(page);
  };

  const handleChangeAccess = async (id, page, action) => {
    try {
      const response = await pageAccess(id, page, action);
      message.success(response?.data?.message);

      setUsers((prevUsers) => {
        return prevUsers?.map((user) => {
          if (user.id === id) {
            let updatedPages;
            if (action === "view") {
              updatedPages = user?.pages?.includes(page)
                ? user.pages
                : [...user.pages, page];
            } else {
              updatedPages = user.pages.filter((p) => p !== page);
            }
            return { ...user, pages: updatedPages };
          }
          return user;
        });
      });

      setLoading(false);
    } catch (error) {
      console.error("Error fetching users:", error);
      setLoading(false);
    }
  };

  const fetchData = async () => {
    try {
      setLoading(true);
      const response = await getCompanyList();
      setUsers(response.data);
      setLoading(false);
    } catch (error) {
      console.error("Error fetching users:", error);
      setLoading(false);
    }
  };

  const fetchAllUsers = async () => {
    try {
      const response = await getUsersList();
      setAllUsers(response.data.filter((user) => user.role !== "company"));
    } catch (error) {
      console.error("Error fetching users:", error);
    }
  };

  useEffect(() => {
    fetchData();
    fetchAllUsers();
  }, []);

  const handleCreateUser = async () => {
    try {
      const values = await form.validateFields();
      setLoading(true);

      const response = await createUser(
        values?.username,
        values?.email,
        values?.role,
        values?.api_key,
        values?.address,
        values?.password,
        values?.parent_company
      );
      if (response?.data) {
        const newUser = response.data;
        setUsers((prevUsers) => [...prevUsers, newUser]);
        setLoading(false);
      }
      form.resetFields();
    } catch (error) {
      console.error("Error fetching users:", error);
      notification.error({
        key: "Error",
        message: "Error",
        description: error?.response?.data?.api_key || "Can't Create User",
        placement: "topRight",
      });
      setLoading(false);
    } finally {
      setIsModalVisible(false);
    }
  };

  const handleEdit = (user) => {
    setEditId(user?.id);
    setEditName(user?.name);
    setEditEmail(user?.email);
    setEditRole(user?.role);
    setEditParentCompany(user?.parent_company);
    setIsModalVisibleEdit(true);
  };

  const handleEditUser = async (values) => {
    try {
      const values = await form2.validateFields();
      setLoading(true);
      const response = await editUser(
        editId,
        values?.username,
        values?.email,
        values?.role,
        values?.api_key,
        values?.parent_company
      );
      if (response.data) {
        message.success(response?.data?.message);
        fetchData();
        form.resetFields();
      }
    } catch (error) {
      console.error("Error updating user:", error);
      notification.error({
        key: "Error",
        message: "Error",
        description: error?.response?.data?.api_key || "Can't Update User",
        placement: "topRight",
      });
    } finally {
      setIsModalVisibleEdit(false);
      setLoading(false);
    }
  };

  const handleChangeStatus = async (id, action) => {
    try {
      setLoading(true);
      const response = await alterUserStatus(id, action);
      message.success(response?.data?.message);
      setLoading(false);
      if (action === "enable") {
        setUsers((prevUsers) =>
          prevUsers?.map((user) =>
            user.id === id ? { ...user, status: "Active" } : user
          )
        );
      } else {
        setUsers((prevUsers) =>
          prevUsers?.map((user) =>
            user.id === id ? { ...user, status: "Inactive" } : user
          )
        );
      }
    } catch (error) {
      console.error("Error fetching users:", error);
      setLoading(false);
    }
  };

  const checkUserStatus = (status) => {
    return status === "Active";
  };

  const handleModalClose = () => {
    setIsModalVisibleEdit(false);
    setIsModalVisible(false);
    form.resetFields();
    form2.resetFields();
  };

  const handleAssignUsers = async (companyId) => {
    if (!selectedUsers.length) {
      message.warning("Please select at least one user");
      return;
    }
    try {
      setIsAssigning(true);
      const response = await bulkAssignParentCompany(companyId, selectedUsers);
      if (response.data) {
        message.success(response.data.message);
        setSelectedUsers([]);
        fetchAllUsers();
      }
    } catch (error) {
      message.error("Failed to assign users");
    } finally {
      setIsAssigning(false);
    }
  };

  const handleRemoveUser = async (userId) => {
    try {
      setLoading(true);
      const userToUpdate = allUsers.find((user) => user.id === userId);
      if (!userToUpdate) {
        message.error("User not found");
        return;
      }

      const response = await editUser(
        userId,
        userToUpdate.name,
        userToUpdate.email,
        userToUpdate.role,
        userToUpdate.api_key,
        null
      );

      if (response.data) {
        message.success("User removed from company successfully");
        fetchAllUsers(); // Refresh the users list
      }
    } catch (error) {
      message.error("Failed to remove user from company");
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleViewClick = (userId, key) => {
    if (activeKey === key) {
      setActiveKey(null);
    } else {
      setActiveKey(key);
    }
  };

  const handleVehicleModal = (id) => {
    navigate(`/vehicles/${id}`);
  };

  const handleDeleteCompany = async (user_id) => {
    setLoadingDelete(true);

    try {
      await deleteUser(user_id);

      setUsers((prevUsers) =>
        prevUsers?.filter((user) => user?.id !== user_id)
      );

      message.success("Company Deleted Successfully");
    } catch (error) {
      console.error("Error deleting user:", error);
    } finally {
      setLoadingDelete(false);
    }
  };

  const checkValidation = async () => {
    try {
      const response = await checkApiKeyValidity();
      setUserApiKeyStatus({
        status: response.data.status,
        message: response.data.message,
      });
    } catch (error) {
      message.error("Failed To Check Api Key Status");
    }
  };

  const handleLoginAsUser = async (id) => {
    try {
      setUserApiKeyStatus(null);
      message.success("Signing out from the admin account");
      const response = await loginAsUser(id);
      localStorage.clear();
      message.success("Logging into the company account");
      localStorage.setItem(
        encryptText("token"),
        encryptText(
          JSON.stringify({
            access: response?.data?.access,
            refresh: response?.data?.refresh,
          })
        )
      );
      localStorage.setItem(
        encryptText("user"),
        encryptText(
          JSON.stringify({
            id: response?.data?.id,
            role: response?.data?.role,
            verified: response?.data?.verified,
            name: response?.data?.name,
            email: response?.data?.email,
            profile_picture: response?.data?.profile_picture,
          })
        )
      );
      updateUserStates(setUserData, setUserToken);
      setUserPages(response?.data?.pages);

      if (response?.data?.verified) {
        if (response?.data?.role !== "admin") {
          await checkValidation();
        }
      } else {
        navigate("/verification-link");
      }
    } catch (error) {
      console.error("Error fetching users:", error);
      setLoading(false);
    }
  };

  return (
    <div className="user-admin-company">
      <div className="page-header">
        <div className="page-title">
          <div className="title">Companies</div>
          <div className="title-description">
            {new Date().toLocaleDateString("en-US", {
              weekday: "long",
              month: "short",
              day: "numeric",
              year: "numeric",
            })}
          </div>
        </div>
        <div className="header-buttons">
          <Button
            className="create-user-button"
            onClick={() => setIsModalVisible(true)}
          >
            Create new Company
          </Button>
        </div>
      </div>
      <Collapse
        bordered={false}
        activeKey={activeKey}
        expandIcon={() => null}
        accordion
        onChange={(key) => setActiveKey(key)}
      >
        {users?.map((user, index) => {
          const assignedUsers = allUsers.filter(
            (u) => u.parent_company === user.id
          );

          return (
            <React.Fragment key={user.id}>
              {/* <div onClick={() => handleEdit(user)} className="edit-section">
                <MdEdit size={20} />
                Edit
              </div> */}
              <Panel
                header={
                  <>
                    <div
                      onClick={(e) => e.stopPropagation()}
                      className="User-information"
                    >
                      <div className="Info-content">
                        <div className="profile-text">Team</div>

                        <div className="profile-info">
                          <div className="compact-avatar">
                            {user?.profile_picture ? (
                              <img
                                className="profile-pic"
                                src={user?.profile_picture}
                                alt="user-icon"
                              />
                            ) : (
                              <UserOutlined className="profile-pic" />
                            )}
                          </div>

                          <div className="profile-details">
                            <div className="profile-name">{user?.name}</div>
                            <div className="profile-email">{user?.email}</div>
                          </div>
                        </div>
                      </div>
                      <div className="profile-actions">
                        <div className="profile-line">
                          <div
                            onClick={() => {
                              handleEdit(user);
                            }}
                            className="edit-section"
                          >
                            <span>
                              <svg
                                width="16"
                                height="16"
                                viewBox="0 0 16 16"
                                fill="none"
                                xmlns="http://www.w3.org/2000/svg"
                              >
                                <path
                                  d="M7.50004 15.1667H14.5834M12.5 6.83333L15 4.33333L11.6667 1L9.16671 3.5M12.5 6.83333L4.16671 15.1667H0.833374V11.8333L9.16671 3.5M12.5 6.83333L9.16671 3.5"
                                  stroke="#3B82F6"
                                  stroke-width="1.5"
                                  stroke-linecap="round"
                                  stroke-linejoin="round"
                                />
                              </svg>
                            </span>
                            Edit
                          </div>
                          <div className="view-section">
                            <Button
                              icon={
                                activeKey === index.toString() ? (
                                  <CaretUpOutlined />
                                ) : (
                                  <CaretDownOutlined />
                                )
                              }
                              onClick={(e) => {
                                e.stopPropagation();
                                handleViewClick(user?.id, index.toString());
                              }}
                            >
                              <span
                                style={{
                                  display: "flex",
                                  alignItems: "center",
                                }}
                              >
                                {activeKey === index.toString()
                                  ? "Close"
                                  : "View"}
                              </span>
                            </Button>
                          </div>
                        </div>
                        <div
                          className={`custom-status-btn ${
                            checkUserStatus(user?.status)
                              ? "active"
                              : "inactive"
                          }`}
                        >
                          {checkUserStatus(user?.status)
                            ? "Active"
                            : "Inactive"}
                        </div>
                      </div>
                    </div>
                  </>
                }
                key={index}
                className="panel-style"
              >
                <div className="panel-content">
                  <div className="section-company">
                    <div className="section-text-company">
                      <div className="section-title-company">
                        Log in as Company
                      </div>
                      <div className="section-description-company">
                        Login as company to see their POV
                      </div>
                    </div>
                    <Button
                      name="action-button login-btn"
                      loading={loading}
                      onClick={() => {
                        handleLoginAsUser(user?.id);
                      }}
                      className="login-btn"
                    >
                      Login As Company
                    </Button>
                  </div>
                  <div className="section-company">
                    <div className="section-text-company">
                      <div className="section-title-company">
                        Vehicle Data Entry
                      </div>
                      <div className="section-description-company">
                        Enter details of your vehicles manually
                      </div>
                    </div>
                    <div className="button-group">
                      <Button
                        className="enable-btn"
                        loading={loading}
                        type="primary"
                        onClick={() => handleVehicleModal(user?.id)}
                      >
                        Add Data
                      </Button>
                    </div>
                  </div>

                  <div className="section-company">
                    <div className="section-text-company">
                      <div className="section-title-company">Assign Users</div>
                      <div className="section-description-company">
                        Select users to assign to this company
                      </div>
                    </div>
                    <div className="assign-users-controls">
                      <Select
                        popupClassName="custom-select-dropdown"
                        showSearch
                        mode="multiple"
                        placeholder="Select Users..."
                        style={{ width: 240, marginLeft: 12 }}
                        value={selectedUsers}
                        onChange={setSelectedUsers}
                        suffixIcon={
                          <SearchOutlined
                            style={{
                              color: "var(--Parimitives-Text-color)",
                            }}
                          />
                        }
                        options={allUsers.map((user) => ({
                          label: user.name,
                          value: user.id,
                        }))}
                        filterOption={(input, option) =>
                          option.label
                            .toLowerCase()
                            .includes(input.toLowerCase())
                        }
                      />
                      <Button
                        className="enable-btn"
                        type="primary"
                        loading={isAssigning}
                        onClick={() => handleAssignUsers(user.id)}
                      >
                        Assign
                      </Button>
                    </div>
                  </div>

                  <div className="section-company">
                    <div className="section-text-company">
                      <div className="section-title-company">
                        Assigned Users
                      </div>
                    </div>
                    <div className="compact-users-container">
                      {assignedUsers.length > 0 ? (
                        assignedUsers.map((user) => (
                          <div key={user.id} className="compact-user-item">
                            <div className="compact-avatar">
                              {user?.profile_picture ? (
                                <img
                                  className="profile-pic"
                                  src={user?.profile_picture}
                                  alt="user-icon"
                                />
                              ) : (
                                <UserOutlined className="profile-pic" />
                              )}
                              <CloseCircleOutlined
                                onClick={() => handleRemoveUser(user.id)}
                                className="remove-user-icon"
                              />
                            </div>
                            <div className="compact-name">{user.name}</div>
                            <div className="compact-name">{user.email}</div>
                          </div>
                        ))
                      ) : (
                        <p>No assigned users</p>
                      )}
                    </div>
                  </div>
                  <div className="section-company">
                    <div className="section-text-company">
                      <div className="section-title-company">
                        Account Status
                      </div>
                      <div className="section-description-company">
                        You can enable or disable accounts of the user
                      </div>
                    </div>
                    <div className="button-group">
                      <Button
                        loading={loading}
                        className={`action-button ${
                          checkUserStatus(user?.status)
                            ? "disable-btn enable-selected"
                            : "enable-btn"
                        }`}
                        disabled={checkUserStatus(user?.status)}
                        onClick={() => handleChangeStatus(user?.id, "enable")}
                      >
                        Enable
                      </Button>
                      <Button
                        loading={loading}
                        className={`action-button ${
                          !checkUserStatus(user?.status)
                            ? "disable-btn disable-selected"
                            : "enable-btn"
                        }`}
                        disabled={!checkUserStatus(user?.status)}
                        onClick={() => handleChangeStatus(user?.id, "disable")}
                      >
                        Disable
                      </Button>
                    </div>
                  </div>
                  <div className="section-company page-access">
                    <div
                      className="section-title-company"
                      style={{ marginBottom: "15px" }}
                    >
                      Pages Access
                    </div>
                    {pages?.map((page, index) => (
                      <div
                        className="section-options"
                        key={page?.value || index}
                      >
                        <div className="section-description">{page?.name}</div>
                        <div className="radio-group">
                          <Radio.Group
                            value={
                              checkPageAccess(page?.value, user?.pages)
                                ? "view"
                                : "restrict"
                            }
                            onChange={(e) =>
                              handleChangeAccess(
                                user?.id,
                                page.value,
                                e.target.value
                              )
                            }
                          >
                            <Radio value="view">View</Radio>
                            <Radio value="restrict">Restrict</Radio>
                          </Radio.Group>
                        </div>
                      </div>
                    ))}
                  </div>
                  <div className="delete-panel">
                    <div className="section-company section-delete">
                      <div className="section-text-company">
                        <div className="section-title-company">
                          Delete Company
                        </div>
                        <div
                          className="section-description-company"
                          style={{ color: "red" }}
                        >
                          This action will remove the company. Continue?
                        </div>
                      </div>
                      <Popconfirm
                        title="Delete the company"
                        description="Are you sure to delete this company?"
                        onConfirm={() => handleDeleteCompany(user?.id)}
                        okText="Yes"
                        cancelText="No"
                        placement="left"
                      >
                        <Button
                          icon={<DeleteOutlined />}
                          loading={loadingDelete}
                          style={{ backgroundColor: "transparent" }}
                          className="delete-btn"
                          danger
                        >
                          Delete
                        </Button>
                      </Popconfirm>
                    </div>
                  </div>
                </div>
              </Panel>
            </React.Fragment>
          );
        })}
      </Collapse>

      <Modal
        className="Edit-modal"
        title="Create New Company Account"
        open={isModalVisible}
        onOk={() => form.submit()}
        onCancel={handleModalClose}
        confirmLoading={loading}
        okText="Create Company"
        cancelText="Cancel"
        okButtonProps={{ className: "user-ok-button" }}
        cancelButtonProps={{ className: "user-cancel-button" }}
      >
        <Form
          layout="vertical"
          onFinish={handleCreateUser}
          form={form}
          className="form"
        >
          <div className="item-wrapper">
            <Form.Item
              label="Username"
              name="username"
              rules={[
                { required: true, message: "Please input the username!" },
              ]}
            >
              <Input />
            </Form.Item>
            <Form.Item
              label="Email"
              name="email"
              rules={[
                {
                  required: true,
                  type: "email",
                  message: "Please input a valid email!",
                },
              ]}
            >
              <Input />
            </Form.Item>
          </div>
          <Form.Item
            label="Address"
            name="address"
            rules={[
              { required: true, message: "Please input the company address!" },
            ]}
          >
            <Input />
          </Form.Item>
          <Form.Item name="role" initialValue="company" hidden>
            <Input type="hidden" />
          </Form.Item>
          <Form.Item
            label="API Key"
            name="api_key"
            rules={[{ required: true }]}
          >
            <Input />
          </Form.Item>
          <div className="password-field">
            <Form.Item
              label="Password"
              name="password"
              rules={[
                { required: true, message: "Please input the password!" },
                { min: 6, message: "Password must be at least 6 characters!" },
              ]}
            >
              <Input.Password />
            </Form.Item>
          </div>
        </Form>
      </Modal>

      <Modal
        className="Edit-modal"
        form={form2}
        title="Edit Company"
        visible={isModalVisibleEdit}
        onCancel={() => handleModalClose()}
        onOk={() => form2.submit()}
        confirmLoading={loading}
        okText="Save Changes"
        cancelText="Cancel"
      >
        <Form
          initialValues={{
            username: editName,
            email: editEmail,
            role: editRole,
            parent_company: editParentCompany,
          }}
          layout="vertical"
          onFinish={handleEditUser}
          className="form"
          form={form2}
        >
          <div className="item-wrapper">
            <Form.Item
              label="Companyname"
              name="username"
              rules={[
                { required: true, message: "Please input the username!" },
              ]}
            >
              <Input />
            </Form.Item>
            <Form.Item
              label="Email"
              name="email"
              rules={[
                {
                  required: true,
                  type: "email",
                  message: "Please input a valid email!",
                },
              ]}
            >
              <Input />
            </Form.Item>
          </div>
          <Form.Item name="role" initialValue="company" hidden>
            <Input type="hidden" />
          </Form.Item>
          <Form.Item
            label="API Key"
            name="api_key"
            rules={[{ required: true }]}
          >
            <Input />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default Companies;
